import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdPlanetInfluenceTranslations, HdPlanetInfoService, HdPlanetPanel } from '../hd-planet-info.service';
import { HdChartPointKey } from '../planet-grid/hd-planet-display-config';
import {
  HdPlanetInfluenceSegment,
  parsePlanetInfluenceMarkers,
} from '../planet-grid/hd-planet-influence-markers.util';
import { HdPlanetCardComponent } from './hd-planet-card.component';

/** Paneel-data: de planeet waarvan de info getoond wordt. */
export interface HdPlanetInfoPanelData {
  /** Planeet of astrologisch punt (AC, DC, MC, IC, Lilith). */
  readonly planetKey: HdChartPointKey;
}

/**
 * Alle invloed-varianten in cheat-sheet-volgorde (zelfde kolommen als de
 * planeet-invloed-cheat-sheet): essentie, rave psychologie, PHS, de drie
 * definitie-situaties en "invloed op".
 */
const HD_PLANET_INFO_INFLUENCE_KEYS = [
  'essence',
  'ravePsychology',
  'phs',
  'inDefinedChannel',
  'inDefinedCenter',
  'inUndefinedCenter',
  'influenceOn',
] as const satisfies readonly (keyof HdPlanetInfluenceTranslations)[];

interface InfluenceRowVm {
  readonly key: string;
  readonly label: string;
  readonly segments: readonly HdPlanetInfluenceSegment[];
}

/**
 * Planeet-info-paneel, geopend via het info-icoon achter de basis-keynote van
 * de activerende planeet in de poort-info (planetaire invloeden): bovenaan de
 * volledige cheat-sheet-kaart van de planeet (zoals de transit-popup en de
 * planeten-cheat-sheet hem tonen), daaronder alle planetaire invloeden van die
 * planeet, niet enkel de situatie van de aangeklikte poort. Een planeet met een
 * verdieping in de planets-scope (`<planeet>.panel`, nu Chiron, die geen
 * invloed-data heeft) toont daarnaast een citaat en extra secties die niet op
 * de kaart passen.
 */
@Component({
  selector: 'hd-planet-info-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdPlanetCardComponent],
  template: `
    @if (planetKey(); as key) {
      <div class="hd-planet-info-panel">
        <hd-planet-card [planetKey]="key" />

        @if (panel(); as deepDive) {
          @if (deepDive.quote) {
            <blockquote class="hd-planet-info-panel__quote" data-test-id="planet-panel-quote">
              {{ deepDive.quote }}
              @if (deepDive.quoteSource) {
                <cite>{{ deepDive.quoteSource }}</cite>
              }
            </blockquote>
          }
          @for (section of deepDive.sections; track $index) {
            <section class="hd-planet-info-panel__section" data-test-id="planet-panel-section">
              <h4 class="hd-planet-info-panel__title">{{ section.title }}</h4>
              @if (section.note) {
                <p class="hd-planet-info-panel__note">{{ section.note }}</p>
              }
              <ul class="hd-planet-info-panel__bullets">
                @for (bullet of section.bullets; track $index) {
                  <li [innerHTML]="bullet"></li>
                }
              </ul>
            </section>
          }
        }

        @if (influenceRows().length > 0) {
          <h4 class="hd-planet-info-panel__title">{{ text().influencesTitle }}</h4>
          <dl class="hd-planet-info-panel__influences">
            @for (row of influenceRows(); track row.key) {
              <div class="hd-planet-info-panel__influence">
                <dt>{{ row.label }}</dt>
                <dd>
                  @for (seg of row.segments; track $index) {
                    @if (seg.role === 'plain') {
                      <span>{{ seg.text }}</span>
                    } @else {
                      <span class="marker-word"
                            [class.marker-personality]="seg.role === 'personality'"
                            [class.marker-design]="seg.role === 'design'">{{ seg.text }}<sup>{{ seg.markerSuperscript }}</sup></span>
                    }
                  }
                </dd>
              </div>
            }
          </dl>
          @if (hasMarkers()) {
            <div class="hd-planet-info-panel__legend">
              <span class="marker-personality"><sup>p</sup> {{ text().personality }}</span>
              <span class="marker-design"><sup>d</sup> {{ text().design }}</span>
            </div>
          }
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .hd-planet-info-panel {
      padding: 12px 14px 16px;
    }

    .hd-planet-info-panel__title {
      margin: 18px 0 8px;
      padding-top: 6px;
      border-top: 2px solid var(--amber, #d4a869);
      color: var(--amber, #d4a869);
      font-family: 'Bw Modelica', sans-serif;
      font-weight: 900;
      font-size: 0.95em;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .hd-planet-info-panel__influences {
      margin: 0;
    }

    .hd-planet-info-panel__quote {
      margin: 16px 4px 0;
      padding: 0 0 0 12px;
      border-left: 3px solid var(--amber, #d4a869);
      font-style: italic;
      font-size: 14px;
      line-height: 1.45;
      color: var(--dark-base, #202020);
    }

    .hd-planet-info-panel__quote cite {
      display: block;
      margin-top: 2px;
      font-size: 12px;
      font-style: normal;
      color: var(--primary-75, #6b7280);
    }

    .hd-planet-info-panel__note {
      margin: 0 0 6px;
      font-size: 12px;
      font-style: italic;
      color: var(--primary-75, #6b7280);
    }

    .hd-planet-info-panel__bullets {
      margin: 0;
      padding-left: 18px;
      display: flex;
      flex-direction: column;
      gap: 5px;
      font-size: 13px;
      line-height: 1.45;
      color: var(--dark-base, #202020);
    }

    .hd-planet-info-panel__influence {
      padding: 6px 0;
      border-bottom: 1px solid var(--separator, #e5e1da);
    }

    .hd-planet-info-panel__influence:last-child {
      border-bottom: none;
    }

    dt {
      margin-bottom: 2px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #2c4a6e;
    }

    dd {
      margin: 0;
      font-size: 13px;
      line-height: 1.45;
      color: var(--dark-base, #202020);
    }

    .marker-word {
      font-weight: 600;
    }

    .marker-personality {
      color: #000;
    }

    .marker-design {
      color: #ff5959;
    }

    sup {
      font-size: 0.7em;
      margin-left: 1px;
      font-weight: 600;
    }

    .hd-planet-info-panel__legend {
      display: flex;
      gap: 12px;
      margin-top: 8px;
      font-size: 11px;
    }
  `,
})
export class HdPlanetInfoPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdPlanetInfoPanelData | null> | null;
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);

  protected readonly planetKey = computed<HdChartPointKey | null>(() => this.panelData?.()?.planetKey ?? null);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly text = computed(() => {
    const lang = this.lang();
    const t = (key: string) => this.transloco.translate<string>(key, undefined, lang);
    return {
      influencesTitle: t('humanDesign.gateInfo.sections.planetaryInfluences'),
      personality: t('humanDesign.planetInfluence.markerLegend.personality'),
      design: t('humanDesign.planetInfluence.markerLegend.design'),
    };
  });

  /** Verdieping die enkel in dit paneel staat (citaat + secties), bv. voor Chiron. */
  protected readonly panel = computed<HdPlanetPanel | null>(() => {
    const key = this.planetKey();
    return key ? this.planetInfo.planetPanelIn(key, this.lang()) : null;
  });

  protected readonly influenceRows = computed<readonly InfluenceRowVm[]>(() => {
    const key = this.planetKey();
    if (!key) return [];
    const lang = this.lang();
    const influence = this.planetInfo.planetInfluenceIn(key, lang)
      ?? (lang === 'nl' ? null : this.planetInfo.planetInfluenceIn(key, 'nl'));
    if (!influence) return [];
    return HD_PLANET_INFO_INFLUENCE_KEYS
      .map(field => ({
        key: field,
        label: this.transloco.translate<string>(`humanDesign.planetInfluence.${field}`, undefined, lang),
        segments: parsePlanetInfluenceMarkers(influence[field]),
      }))
      .filter(row => row.segments.length > 0);
  });

  protected readonly hasMarkers = computed(() =>
    this.influenceRows().some(row => row.segments.some(segment => segment.role !== 'plain')),
  );
}
